"use client";

// 1280×720 cap: drawImage downsamples source frames — mitigates CPU load on older hardware.
// RAF is suspended when document.hidden; MediaRecorder continues encoding duplicate frames.
// canvas.captureStream() returns a CanvasCaptureMediaStreamTrack — MIME fallback table covers it.
// When enabled === false: returns null immediately, no canvas or RAF created.

import { useEffect, useRef, useState } from "react";

export type PipPosition = "TL" | "TR" | "BL" | "BR";
export type PipSize = "S" | "M" | "L";

export interface UseScreenShareComposerOptions {
  screenStream: MediaStream | null;
  cameraStream: MediaStream | null;
  position: PipPosition;
  size: PipSize;
  enabled: boolean;
}

export interface UseScreenShareComposerReturn {
  composedStream: MediaStream | null;
  isComposing: boolean;
}

export const CANVAS_W = 1280;
export const CANVAS_H = 720;

export const SIZE_MAP: Record<PipSize, [number, number]> = {
  S: [240, 135],
  M: [320, 180],
  L: [480, 270],
};

export function positionToXY(
  position: PipPosition,
  pipW: number,
  pipH: number,
  canvasW: number,
  canvasH: number,
  margin = 16,
): [number, number] {
  switch (position) {
    case "TL": return [margin, margin];
    case "TR": return [canvasW - pipW - margin, margin];
    case "BL": return [margin, canvasH - pipH - margin];
    case "BR": return [canvasW - pipW - margin, canvasH - pipH - margin];
  }
}

export function useScreenShareComposer({
  screenStream,
  cameraStream,
  position,
  size,
  enabled,
}: UseScreenShareComposerOptions): UseScreenShareComposerReturn {
  const [composedStream, setComposedStream] = useState<MediaStream | null>(null);
  const [isComposing, setIsComposing] = useState(false);

  const rafRef = useRef<number | null>(null);
  const composedStreamRef = useRef<MediaStream | null>(null);
  const isComposingRef = useRef(false);

  useEffect(() => {
    if (!enabled || !screenStream) return;

    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_W;
    canvas.height = CANVAS_H;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const screenVideoEl = document.createElement("video");
    screenVideoEl.srcObject = screenStream;
    screenVideoEl.muted = true;
    screenVideoEl.playsInline = true;

    const cameraVideoEl = document.createElement("video");
    if (cameraStream) {
      cameraVideoEl.srcObject = cameraStream;
      cameraVideoEl.muted = true;
      cameraVideoEl.playsInline = true;
    }

    let captureStream: MediaStream | null = null;

    const draw = () => {
      // Suspend RAF when tab is hidden to avoid wasting CPU on invisible frames.
      if (document.hidden) return;

      ctx.drawImage(screenVideoEl, 0, 0, CANVAS_W, CANVAS_H);

      if (cameraStream && cameraVideoEl.readyState >= 2) {
        const [pipW, pipH] = SIZE_MAP[size];
        const [x, y] = positionToXY(position, pipW, pipH, CANVAS_W, CANVAS_H);
        ctx.drawImage(cameraVideoEl, x, y, pipW, pipH);
      }

      rafRef.current = requestAnimationFrame(draw);

      if (!captureStream) {
        captureStream = canvas.captureStream(30);
        screenStream.getAudioTracks().forEach((t) => captureStream!.addTrack(t));
        if (cameraStream) {
          cameraStream.getAudioTracks().forEach((t) => captureStream!.addTrack(t));
        }
        composedStreamRef.current = captureStream;
        setComposedStream(captureStream);
        setIsComposing(true);
        isComposingRef.current = true;
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && isComposingRef.current) {
        rafRef.current = requestAnimationFrame(draw);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const startCompositor = async () => {
      await screenVideoEl.play().catch(() => {});
      if (cameraStream) {
        await cameraVideoEl.play().catch(() => {});
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    startCompositor();

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange);

      // Stopping the captureStream video track signals MediaRecorder end-of-stream.
      // Caller must have stopped MediaRecorder before this cleanup runs.
      composedStreamRef.current?.getTracks().forEach((t) => t.stop());
      composedStreamRef.current = null;

      screenVideoEl.srcObject = null;
      cameraVideoEl.srcObject = null;

      isComposingRef.current = false;
      setComposedStream(null);
      setIsComposing(false);
    };
  }, [enabled, screenStream, cameraStream, position, size]);

  if (!enabled) {
    return { composedStream: null, isComposing: false };
  }

  return { composedStream, isComposing };
}
